// src/cockpit4/components/WorkspaceBizCrumb.tsx
// ============================================
// Phase 3 P2：业务链路面包屑
//
// 定位：进入任意工作区后，顶部展示「业务链路」而非仅页面层级。
//   例：总控台 > 业务主线：获客 > GEO+DSO 挖掘
// 数据源：参照 UX 文档 §二.4 与 §三 业务闭环主链路（获客→内容→发布→线索→成交→复盘）
//   6 节点：线索挖掘 → 内容生产 → 发布引流 → 线索入池 → 洽谈成交 → 复盘迭代
// 说明：仅对 super_admin/老板 展示完整链路，业务员工折叠为「当前模块」单点，
//       避免技术/流程信息过度暴露（呼应 §四.1 技术报错对业务员工折叠）。
// ============================================

import { useMemo } from "react";
import { Home } from "lucide-react";
import { useCockpit4Store, type WorkspaceId } from "../store";
import { useUserStore } from "../../stores/userStore";
// Phase3 P2-2/2-3：全局模块帮助入口（覆盖未挂 biz bar 的模块）
import WorkspaceHelp from "./WorkspaceHelp";

// ============================================
// 业务主线分段标签（当前工作区 → 所属业务环节）
// ============================================

interface CrumbStep {
  label: string; // 业务环节名
  to: WorkspaceId; // 点击跳转目标
}

/** 每个工作区在业务链路中的展开（前序环节 + 当前环节 + 后续环节） */
const CRUMB_CHAIN: Partial<Record<WorkspaceId, CrumbStep[]>> = {
  ceo: [{ label: "总控台", to: "ceo" }],
  geodso: [
    { label: "业务主线：获客", to: "geodso" },
    { label: "线索挖掘", to: "geodso" },
  ],
  media_studio: [
    { label: "业务主线：内容", to: "media_studio" },
    { label: "内容生产", to: "media_studio" },
  ],
  marketing: [
    { label: "业务主线：内容", to: "marketing" },
    { label: "发布引流", to: "marketing" },
  ],
  crm: [
    { label: "业务主线：成交", to: "crm" },
    { label: "线索入池 · 洽谈成交", to: "crm" },
  ],
  finance: [
    { label: "业务主线：成交", to: "finance" },
    { label: "回款记账", to: "finance" },
  ],
  workflow: [
    { label: "业务主线：复盘", to: "workflow" },
    { label: "复盘迭代", to: "workflow" },
  ],
  knowledge: [{ label: "知识底座", to: "knowledge" }],
  projects: [{ label: "AI 超市", to: "projects" }],
  boss_view: [{ label: "老板视图", to: "boss_view" }],
  settings: [{ label: "设置", to: "settings" }],
};

// 工作区显示名（与 WorkspaceHeader4 对齐，供后续环节展示）
const TAB_LABEL: Record<WorkspaceId, string> = {
  ceo: "总控台",
  marketing: "营销中心",
  media_studio: "内容工厂",
  media_workbench: "自媒体",
  crm: "客户中心",
  knowledge: "知识中心",
  projects: "AI 超市",
  finance: "财务中心",
  workflow: "经营中心",
  geodso: "GEO+DSO",
  boss_view: "老板视图",
  settings: "设置",
};

export default function WorkspaceBizCrumb() {
  const currentWorkspace = useCockpit4Store((s) => s.currentWorkspace);
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const currentUser = useUserStore((s) => s.currentUser);

  const isFull = currentUser?.role === "super_admin";

  const steps = useMemo<CrumbStep[]>(() => {
    const chain = CRUMB_CHAIN[currentWorkspace] ?? [{ label: TAB_LABEL[currentWorkspace] ?? "总控台", to: "ceo" }];
    // 业务员工：只看「当前模块」单点，折叠主链路
    return isFull ? chain : [{ label: TAB_LABEL[currentWorkspace] ?? "总控台", to: currentWorkspace }];
  }, [currentWorkspace, isFull]);

  return (
    <div className="flex items-center gap-1.5 px-4 py-1 text-[10px] font-mono text-gray-500 border-b border-white/[0.04] bg-white/[0.01]">
      {/* 首页锚点 */}
      <button
        onClick={() => setWorkspace("ceo")}
        className="flex items-center gap-1 hover:text-amber-400 transition-colors"
      >
        <Home size={11} />
        <span>总控台</span>
      </button>

      {steps.map((s, i) => (
        <span key={`${s.label}-${i}`} className="flex items-center gap-1.5">
          <span className="text-gray-700">/</span>
          <button
            onClick={() => setWorkspace(s.to)}
            className={`hover:text-white transition-colors ${
              i === steps.length - 1 ? "text-cyan-300" : "text-gray-500"
            }`}
          >
            {s.label}
          </button>
        </span>
      ))}

      {/* Phase3 P2-2/2-3：模块帮助 + 学习文档（右侧），未配置的模块自动隐藏 */}
      <div className="ml-auto -mr-1">
        <WorkspaceHelp />
      </div>
    </div>
  );
}